import React, { useEffect, useState } from "react";
import moment from "moment";
import de from "moment/locale/de";
import { Provider, useSelector } from "react-redux";
import { ThemeProvider, createGlobalStyle } from "styled-components";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { GlobalStyles, LayoutAdminDefault, PaginationTableHelper } from "@webtypen/react-components";
import { store } from "./redux";
import { defaultTheme } from "./theme";
import { AppStyle, ConfirmWrapper, Logo } from "./components";
import { Login } from "./screens/Login";
import { Error404 } from "./screens/Error404";
import { Dashboard } from "./screens/Dashboard";
import {
    faChampagneGlasses,
    faClockRotateLeft,
    faFileContract,
    faGaugeHigh,
    faHouseChimneyUser,
    faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { CustomersOverview } from "./screens/customers/CustomersOverview";
import { ApiHelper, AuthHelper } from "./helpers";
import { CustomersEdit } from "./screens/customers/CustomersEdit";
import { EmployeesOverview } from "./screens/employees/EmployeesOverview";
import { EmployeesEdit } from "./screens/employees/EmployeesEdit";
import { ContractsOverview } from "./screens/contracts/ContractsOverview";
import { ContractsEdit } from "./screens/contracts/ContractsEdit";
import { LayoutUsermenu } from "./components/LayoutUsermenu";
import { EmployeesShiftsOverview } from "./screens/employees-shifts/EmployeesShiftsOverview";
import { EmployeesShiftsDetails } from "./screens/employees-shifts/EmployeesShiftsDetails";
import { ChangePassword } from "./screens/ChangePassword";
import { ContractsDetails } from "./screens/contracts/ContractsDetails";
import { ContractRequest } from "./screens/contracts/ContractRequest";
import { ContractRequestsList } from "./screens/contracts/ContractRequestsList";
import { WorkingHoursOverview } from "./screens/working-hours/WorkingHoursOverview";

// moment
moment.updateLocale("de", de);

function App() {
    return (
        <Provider store={store}>
            <ThemeProvider theme={defaultTheme}>
                <GlobalStyles />
                <AppStyle />

                <BrowserRouter basename={process.env.REACT_APP_BASENAME ? process.env.REACT_APP_BASENAME : undefined}>
                    <ConfirmWrapper />

                    <Routes>
                        <Route path="/" element={<Login />} />
                        <Route path="*" element={<AppArea />} />
                    </Routes>
                </BrowserRouter>
            </ThemeProvider>
        </Provider>
    );
}

const AppArea = () => {
    const auth = useSelector((state) => state.app.auth);
    const navigate = useNavigate();
    const [inited, setInited] = useState(false);

    useEffect(() => {
        if (auth) {
            ApiHelper.request("/api/auth/auth-data")
                .then((response) => {})
                .catch((e) => {
                    console.error(e);
                    AuthHelper.logout();
                    navigate("/");
                });
        }
    }, [auth]);

    useEffect(() => {
        PaginationTableHelper.request = (url, payload) => {
            return ApiHelper.request(url, { ...payload });
        };
        setInited(true);
    }, []);

    if (!auth) {
        return (
            <Routes>
                <Route path="*" element={<Error404 />} />
            </Routes>
        );
    }

    return (
        <LayoutAdminDefault
            logo={<Logo />}
            menu={
                auth.type === "admin"
                    ? [
                          { icon: faGaugeHigh, label: "Dashboard", path: "/dashboard" },
                          { icon: faFileContract, label: "Aufträge", path: "/contracts" },
                          { icon: faFileContract, label: "Auftragsanfragen", path: "/contract-request/list" },
                          { icon: faClockRotateLeft, label: "Arbeitszeiten", path: "/working-hours/list" },
                          { icon: faHouseChimneyUser, label: "Kunden", path: "/customers" },
                          { icon: faUsers, label: "Mitarbeiter", path: "/employees" },
                      ]
                    : auth.type === "employee"
                    ? [
                          { icon: faGaugeHigh, label: "Dashboard", path: "/dashboard" },
                          { icon: faChampagneGlasses, label: "Jobs / Schichten", path: "/employees-shifts" },
                      ]
                    : auth.type === "customer_person"
                    ? [
                          { icon: faGaugeHigh, label: "Dashboard", path: "/dashboard" },
                          { icon: faFileContract, label: "Aufträge", path: "/contracts" },
                      ]
                    : null
            }
            menuAppBar={
                <>
                    <li>
                        <LayoutUsermenu />
                    </li>
                </>
            }
        >
            {inited ? (
                <Routes>
                    {/* Dashboard */}
                    <Route path="/dashboard" element={<Dashboard />} />

                    {/* Auth-Routen */}
                    <Route path="/change-pw" element={<ChangePassword />} />

                    {/* Admin-Routen */}
                    <Route path="/customers" element={<CustomersOverview />} />
                    <Route path="/customers/:id/edit" element={<CustomersEdit />} />
                    <Route path="/employees" element={<EmployeesOverview />} />
                    <Route path="/employees/:id/edit" element={<EmployeesEdit />} />
                    <Route path="/contract-request" element={<ContractRequest />} />
                    <Route path="/contract-request/list" element={<ContractRequestsList />} />
                    <Route path="/contracts" element={<ContractsOverview />} />
                    <Route path="/contracts/:id/details" element={<ContractsDetails />} />
                    <Route path="/contracts/:id/edit" element={<ContractsEdit />} />
                    <Route path="/working-hours/list" element={<WorkingHoursOverview />} />

                    {/* Mitarbeiter-Routen */}
                    <Route path="/employees-shifts" element={<EmployeesShiftsOverview />} />
                    <Route path="/employees-shifts/:id/details" element={<EmployeesShiftsDetails />} />

                    {/* 404 */}
                    <Route path="*" element={<Error404 />} />
                </Routes>
            ) : null}
        </LayoutAdminDefault>
    );
};

export default App;
